import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import {
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SESSION_ACTIVITY_TYPE_DESK,
    SESSION_ACTIVITY_TYPE_MOVING,
    SessionActivityType,
} from '@app/core/_models'
import { PartnerSessionPreferences } from '@app/core/_services/available-times.service'
import {
    SessionStateService,
    SESSION_STATE_LATE,
    SESSION_STATE_SEARCHING,
} from '@app/core/_services/session-state.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { UserService } from '@app/core/_services/user.service'
import { faLock, faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons'
import { FmAvatarComponent } from '@app/shared/components/fm-avatar/fm-avatar.component'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ButtonModule } from 'primeng/button'
import { faLampDesk, faPersonWalking, faShuffle } from '@fortawesome/pro-solid-svg-icons'
import { ActionsMenuAvailabilityListComponent } from '@app/routes/availability/favorites/components/actions-menu-availability-list/actions-menu-availability-list.component'

export interface AvailabilityListBookSession {
    sessionTime: number
    duration: number
    userId: string
    activityType: SessionActivityType
}

export interface AvailabilityListCancelSession {
    externalId: string
    sessionTime: number
    userId: string
}

export interface AvailabilityListSelectedSession {
    sessionTime: number
    sessionDuration: number
    userId: string
}

export interface AvailabilitySessionListItem {
    timeLabel: string
    externalId: string | null
    sessionTime: number
    sessionDuration: number
    sessionState: number | null
    sessionDurationLabel: string
    actionComplete: boolean
    confirmed: boolean
    cancelConfirm: boolean
    profilePhoto: string
    partnerUserId: string
    partnerDisplayName: string
    partnerProfileSlug: string
    partnerPreferences: PartnerSessionPreferences
    quietModeMatchAllowed: boolean
    saved: boolean
    sharedAvailability: boolean
    activityType: number
}

type ButtonState =
    | 'bookedMatched'
    | 'bookedNoPartner'
    | 'available'
    | 'confirmCancel'
    | 'viewingSelf'

const BUTTON_TEXT_MATCHING = 'Matching...'
const BUTTON_TEXT_SEARCHING = 'Searching...'
const BUTTON_TEXT_LATE = 'Late'

@Component({
    selector: 'fm-user-availability-list-item',
    templateUrl: './user-availability-list-item.component.html',
    styleUrls: ['./user-availability-list-item.component.scss'],
    imports: [
        CommonModule,
        FmAvatarComponent,
        ActionsMenuAvailabilityListComponent,
        FontAwesomeModule,
        MatTooltipModule,
        ButtonModule,
    ],
})
export class UserAvailabilityListItemComponent implements OnInit {
    @Input() externalId: string
    @Input() sessionTime: number
    @Input() sessionDuration: number
    @Input() sessionState: number | null = null
    @Input() sessionActivityType: SessionActivityType
    @Input() durationLabel: string
    @Input() sessionTimeLabel: string
    @Input() booked: boolean
    @Input() selected: boolean
    @Input() bookingComplete: boolean
    @Input() disable: boolean
    @Input() viewingSelf: boolean = false
    @Input() userId: string
    @Input() displayName: string
    @Input() profileSlug: string
    @Input() profilePhoto: string
    @Input() saved: boolean = false
    @Input() useTimeLabel: boolean = false
    @Input() partnerQuietMode: boolean = false
    @Input() dynamicPanel: boolean = false
    @Output() onCancelSession = new EventEmitter<AvailabilityListCancelSession>()
    @Output() onBookSession = new EventEmitter<AvailabilityListBookSession>()
    @Output() onSelectSession = new EventEmitter<AvailabilityListSelectedSession>()

    constructor(
        private sessionStateService: SessionStateService,
        public userService: UserService,
    ) {}

    public faLock = faLock
    public faMicrophoneSlash = faMicrophoneSlash
    public confirmCancel = false
    public cancelInProgress = false
    public buttonText: string = ''
    public isLockedIn: boolean = false

    public buttonState: ButtonState = 'available'
    public activityIcon

    ngOnInit(): void {
        this.setState()
        this.setActivityIcon()
    }

    ngOnChanges(e) {
        if (e.hasOwnProperty('booked')) {
            if (e.booked.previousValue === true && e.booked.currentValue == false) {
                this.confirmCancel = false
                this.cancelInProgress = false
            }
        }
        this.setState()
        this.setActivityIcon()
    }

    setState() {
        this.isLockedIn = this.sessionStateService.isLockedInState(this.sessionState)
        if (this.viewingSelf) {
            this.buttonState = 'viewingSelf'
        } else if (this.confirmCancel) {
            this.buttonState = 'confirmCancel'
        } else if (this.booked) {
            if (this.displayName) {
                this.buttonText = this.displayName
                this.buttonState = 'bookedMatched'
            } else {
                if (this.sessionStateService.isPendingPartnerState(this.sessionState)) {
                    this.buttonText = BUTTON_TEXT_MATCHING
                } else if (this.sessionState === SESSION_STATE_SEARCHING) {
                    this.buttonText = BUTTON_TEXT_SEARCHING
                } else if (this.sessionState === SESSION_STATE_LATE) {
                    this.buttonText = BUTTON_TEXT_LATE
                } else {
                    // Shouldn't get here but if we do, indicate matching
                    this.buttonText = BUTTON_TEXT_MATCHING
                }
                this.buttonState = 'bookedNoPartner'
            }
        } else {
            this.buttonState = 'available'
        }
    }

    onKeepSession(e: Event) {
        e.stopPropagation()
        this.confirmCancel = false
        this.setState()
    }

    onCancelSessionSelected(e: Event) {
        this.confirmCancel = true
        this.setState()
    }

    onCancelConfirmed() {
        this.cancelInProgress = true
        this.onCancelSession.emit({
            externalId: this.externalId,
            sessionTime: this.sessionTime,
            userId: this.userId,
        })
        this.setState()
    }

    bookSession(e: Event) {
        e.stopPropagation()
        this.onBookSession.emit({
            sessionTime: this.sessionTime,
            duration: this.sessionDuration,
            userId: this.userId,
            activityType: this.sessionActivityType,
        })
    }

    selectSession(e: Event) {
        e.stopPropagation()
        this.onSelectSession.emit({
            sessionTime: this.sessionTime,
            userId: this.userId,
            sessionDuration: this.sessionDuration,
        })
    }

    private setActivityIcon(): void {
        switch (this.sessionActivityType) {
            case SESSION_ACTIVITY_TYPE_ANYTHING:
                this.activityIcon = faShuffle
                break

            case SESSION_ACTIVITY_TYPE_DESK:
                this.activityIcon = faLampDesk
                break

            case SESSION_ACTIVITY_TYPE_MOVING:
                this.activityIcon = faPersonWalking
                break
        }
    }
}
